import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Infinity as InfinityIcon, Timer } from 'lucide-react';

import { env } from '@/env';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { useHaptics } from '@/hooks/useHaptics';
import { useRankedQueue, type RankedQueueStatus } from '@/hooks/useRankedQueue';
import { useStartMatch } from '@/hooks/useStartMatch';
import { RankedQueueSheet } from '@/components/backgammon/RankedQueueSheet';
import { queueLabelKey } from '@/lib/qgammon/queueMode';
import { cn } from '@/lib/cn';

/** Il server allarga la finestra d'abbinamento da ±50 a ±200 ELO dopo questo tempo. */
const WIDEN_AFTER_MS = 30_000;

/** m:ss per il tempo d'attesa. */
function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Da quanto si aspetta, un tick al secondo. Ancorato all'ora d'ingresso CONFERMATA dal server
 * (`mm:queued`, o `now - waitingMs` dalla `GET /matchmaking/queue` sul ramo `409`) quando c'e',
 * altrimenti all'istante in cui la ricerca e' partita qui.
 */
function useTempoInCoda(status: RankedQueueStatus, enqueuedAt: number | null): number {
  const [elapsed, setElapsed] = useState(0);
  const cercando = status === 'queueing' || status === 'queued';
  useEffect(() => {
    if (!cercando) {
      setElapsed(0);
      return;
    }
    const start = enqueuedAt ?? Date.now();
    setElapsed(Date.now() - start);
    const id = window.setInterval(() => setElapsed(Date.now() - start), 1_000);
    return () => window.clearInterval(id);
  }, [cercando, enqueuedAt]);
  return elapsed;
}

interface RankedQueueEntryProps {
  /** L'ELO da `/me/stats` (o dall'identita'); `null` = non si scrive. */
  elo?: number | null;
  /** La posizione: SOLO se `/me/stats` ha risposto; `null` = non si scrive. */
  rank?: number | null;
  /**
   * Cio' che sta SOTTO la card — la griglia delle tessere (I-282). Riceve `inerte`: vero mentre una
   * ricerca e' in corso (o si e' appena chiusa con una coppia), quando le tessere si attenuano e non
   * si premono; e `libera`, l'ingresso nella coda senza orologio e fuori classifica, ASSENTE a
   * serratura spenta. E' una funzione e non un figlio perche' lo stato della coda vive QUI, e
   * sollevarlo nel pannello vorrebbe dire due proprietari per lo stesso socket.
   */
  altri?: (stato: { inerte: boolean; libera?: () => void }) => ReactNode;
}

/**
 * La card «Ranked» del backgammon — reference approvata «Anello che respira» (Luca, I-277, scelta
 * **L-2606 (a)**, 30/09; KB `services/qlash-telegram-app/ui/2026-09-30-backgammon-ranked-anello.md`,
 * tavole `A.dc.html` / `A-coda.dc.html` / `Foglio-conferma.dc.html`):
 *
 *  - **L'ANELLO E' IL BOTTONE della classificata.** Dentro: «ELO 1532 · 12°» (valori veri), «GIOCA»
 *    e «Classificata · con orologio». Da fermo respira. Il tap apre la conferma di L-782 (copy
 *    invariata), «Entra in classificata» entra nella coda CON orologio (`{}` sul filo).
 *    ⛔ **La classificata esiste SOLO con l'orologio** (I-277): la pillola «Classificata · senza
 *    orologio» non c'e' piu'.
 *  - **In coda, la ricerca sta DENTRO l'anello**: un arco gira (un'ATTESA, non un progresso), il
 *    tempo, «Cerco un avversario…» e la coda; «Annulla» sotto. Il foglio (`RankedQueueSheet`) resta
 *    solo per cio' che viene DOPO: l'avviso di una ricerca fermata e il breve «Trovato!».
 *  - **La coda «Senza orologio · fuori classifica»** entra SENZA conferma — la conferma di L-782
 *    esiste perche' il tap scrive ELO, e qui l'ELO non si muove. ~~Era una pill sotto l'anello~~: dal
 *    01/10 (Luca, I-282) e' una TESSERA della griglia sotto la card, accanto a bot, amico e codice, e
 *    il suo ingresso arriva a quella griglia come `libera` (vedi `altri`). La sua RICERCA sta nell'anello
 *    come quella della classificata, con la sua etichetta. Dietro
 *    `env.featureBackgammonUnrankedQueueEnabled`: spenta, la tessera NON c'e'.
 *  - 🔴 **Niente etichetta «Ranked» in testa alla card** (I-282): la scheda e' diventata «Gioca».
 *
 * ⚠️ L'anello non misura niente: il backgammon non ha tier, e un arco parziale da fermi disegnerebbe
 * una misura che non esiste. Il contorno e' pieno; l'arco che gira in coda dice «sto aspettando».
 * ⚠️ Le code funzionano anche se statistiche e storico non rispondono: senza ELO l'anello resta un
 * bottone, semplicemente senza la riga in alto.
 */
export function RankedQueueEntry({ elo = null, rank = null, altri }: RankedQueueEntryProps) {
  const { t } = useTranslation('backgammon');
  const haptics = useHaptics();
  const startMatch = useStartMatch();
  /**
   * 🔴 LA CODA CLASSIFICATA SI CONFERMA — decisione di Luca, `L-782` (11/09 12:40:54Z,
   * opzione «C) Entrambe»: *conferma* **E** *le pillole classificate cambiano forma/colore*).
   *
   * Il motivo, misurato: i due bottoni «Classificata» erano **identici in forma** a
   * «Gioca contro il bot» e a ~150 px da esso, ma un tap **scrive ELO permanente**
   * (±21 anche a zero mosse) mentre quello accanto non scrive niente. `or45` lo premette
   * per errore il 07/09 e si salvo' **solo perche' il token era rifiutato — oggi non lo e' piu'.**
   * ⇒ ***Un tap che scrive un risultato irreversibile non deve avere la stessa forma di uno
   * che non scrive.***
   * 📌 Dal 30/09 la classificata e' UNA (con orologio), quindi la conferma e' un booleano: il foglio
   * nomina comunque la coda, perche' dire in quale coda si entra resta parte di COSA si scrive.
   * La forma: l'anello e' un CERCHIO a contorno, la pratica e' una tessera (qui) o un bottone pieno
   * (nel foglio del bot) — `tools/verify-classificata-forma.ts`.
   */
  const [conferma, setConferma] = useState(false);
  useLivelloIndietro(conferma, () => setConferma(false));
  const ranked = useRankedQueue();
  const elapsed = useTempoInCoda(ranked.status, ranked.enqueuedAt);

  const cercando = ranked.status === 'queueing' || ranked.status === 'queued';
  const occupato = ranked.status !== 'idle';
  // La ricerca in corso e' un livello: indietro = «Annulla», come quando stava nel foglio. Con un
  // avviso o a coppia fatta il livello e' del foglio (`RankedQueueSheet`), non di questo.
  useLivelloIndietro(cercando && !ranked.notice, ranked.cancel);

  useEffect(() => {
    if (ranked.status !== 'paired' || !ranked.paired) return;
    haptics.notification('success');
    const { matchId, color } = ranked.paired;
    const timer = window.setTimeout(() => {
      startMatch({ game: 'qgammon', matchId, perspective: color });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [ranked.status, ranked.paired, startMatch, haptics]);

  /** Il tap sull'anello CHIEDE: l'ingresso vero e' `confermaIngresso`. */
  function chiediConferma() {
    haptics.impact('light');
    setConferma(true);
  }

  function confermaIngresso() {
    if (!conferma) return;
    haptics.impact('medium');
    ranked.join('clocked');
    setConferma(false);
  }

  /** La coda libera ENTRA subito: nessun ELO in gioco, nessuna conferma (v. il blocco in testa). */
  function entraLibera() {
    haptics.impact('light');
    ranked.join('unlimited');
  }

  const cercaTesto = elapsed >= WIDEN_AFTER_MS ? t('ranked.searchWide') : t('ranked.searching');
  // QUALE coda si sta cercando: la dice l'hook. ⚠️ Un modo assente durante una ricerca non e' la
  // coda libera: si nomina la classificata, che e' la coda di sempre e quella del `{}`.
  const modoInCoda = ranked.mode === 'unlimited' ? 'unlimited' : 'clocked';
  const IconaCoda = modoInCoda === 'unlimited' ? InfinityIcon : Timer;

  return (
    <>
      {/* 🔴 Dal 01/10 (Luca, I-282) la card NON ha piu' l'etichetta «Ranked» in testa: la scheda si
          chiama «Gioca», e l'anello dice da se' cosa scrive («Classificata · con orologio»). */}
      <section
        data-testid="bg-ranked-card"
        className="gioca-bg-card card-edge flex flex-col items-center justify-center gap-4 rounded-3xl bg-bg-secondary"
      >
        {/* Il diametro (`--d`) lo decide l'altezza che resta alla card (I-334 D): v. `.gioca-bg-anello`. In coda, su una
            card bassa, «Annulla» va ACCANTO all'anello invece che sotto (`.gioca-bg-dentro`). */}
        <div className="gioca-bg-dentro" data-in-coda={occupato || undefined}>
          <div className="gioca-bg-anello relative grid shrink-0 place-items-center" data-in-coda={occupato || undefined}>
            {occupato ? (
              /* ── IN CODA: la ricerca DENTRO l'anello, per tutte e due le code (I-282: anche la libera,
                 che prima cercava nella sua pill) ── */
              <div
                data-testid="bg-ranked-cerca"
                data-coda={modoInCoda}
                className="absolute inset-0 grid place-items-center"
              >
                <svg aria-hidden viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
                  <circle
                    cx="50"
                    cy="50"
                    r="46"
                    fill="none"
                    strokeWidth="2.6"
                    className="stroke-brand/20"
                  />
                </svg>
                {/* L'arco che gira: un'ATTESA, non un progresso (nessun «quanto manca»). Fermo con
                    `prefers-reduced-motion`: il tempo che scorre al centro dice gia' che si aspetta. */}
                <svg
                  aria-hidden
                  viewBox="0 0 100 100"
                  className="absolute inset-0 h-full w-full motion-safe:animate-spin motion-safe:[animation-duration:1.4s]"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="46"
                    fill="none"
                    strokeWidth="3.6"
                    strokeLinecap="round"
                    strokeDasharray="72 217"
                    className="stroke-brand"
                  />
                </svg>
                <div className="gioca-bg-cerchio-testo flex flex-col items-center text-center">
                  <span
                    data-testid="bg-ranked-attesa"
                    className="gioca-bg-attesa font-display font-bold tabular-nums text-brand"
                  >
                    {formatElapsed(elapsed)}
                  </span>
                  {/* Il live region e' la FRASE, non il tempo: un contatore annunciato ogni secondo
                      sarebbe rumore per chi usa un lettore di schermo. */}
                  <span role="status" className="gioca-bg-cerca font-semibold text-fg">
                    {cercaTesto}
                  </span>
                  {/* ⚠️ QUALE coda, non solo «cerco»: le due non si incontrano mai, e un'attesa nella
                      coda sbagliata e' indistinguibile da una coda vuota. L'etichetta della libera e'
                      la piu' lunga: va a capo dentro il cerchio invece di uscirne. */}
                  <span className="gioca-bg-coda text-center leading-snug text-fg-muted">
                    <IconaCoda
                      aria-hidden
                      className="mr-1 inline-block h-3.5 w-3.5 -translate-y-px align-middle"
                    />
                    {t(queueLabelKey(modoInCoda))}
                  </span>
                </div>
              </div>
            ) : (
              <>
                {/* L'alone che si allarga: solo da FERMI, e solo con il movimento permesso. */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-2 rounded-full border-2 border-brand/50 opacity-0 motion-safe:animate-anello-alone"
                />
                <button
                  type="button"
                  data-testid="ranked-join-clocked"
                  onClick={chiediConferma}
                  aria-label={t('ranked.ringAria')}
                  /* 🔴 CONTORNO, NON PIENO — `L-782`, e non e' estetica. La pratica (il bot) e' un
                     bottone PIENO: e' l'azione che non scrive niente. La classificata e' un CERCHIO a
                     contorno: si distingue per FORMA, non solo per colore, che un daltonico non vede e un
                     telefono al sole nemmeno. Il divieto sta in `tools/verify-classificata-forma.ts`. */
                  className={cn(
                    'gioca-bg-bottone relative flex flex-col items-center justify-center rounded-full border-[6px] border-brand',
                    'bg-[radial-gradient(circle_at_50%_38%,var(--color-bg-tertiary)_0%,var(--color-bg-secondary)_62%,var(--color-bg)_100%)]',
                    'transition-opacity motion-safe:animate-anello-respira active:opacity-80',
                  )}
                >
                  {elo !== null && (
                    <span className="gioca-bg-elo text-center font-bold leading-tight text-fg-muted">
                      {t('home.elo')}{' '}
                      <span data-testid="bg-ranked-elo" className="tabular-nums text-fg">
                        {elo}
                      </span>
                      {rank !== null && (
                        <>
                          {' · '}
                          <span data-testid="bg-ranked-rank" className="tabular-nums">
                            {t('hubPanels.rankPosition', { n: rank, defaultValue: '#{{n}}' })}
                          </span>
                        </>
                      )}
                    </span>
                  )}
                  <span className="gioca-bg-gioca font-display font-extrabold leading-tight tracking-tight text-brand">
                    {t('ranked.play')}
                  </span>
                  <span className="gioca-bg-sottotitolo text-center font-semibold leading-tight text-fg-muted">
                    {/* L'icona IN LINEA col testo: quando la riga va a capo (anello piccolo) resta attaccata alla parola. */}
                    <Timer aria-hidden className="mr-1.5 inline-block h-3.5 w-3.5 -translate-y-px align-middle" />
                    {t(queueLabelKey('clocked'))}
                  </span>
                </button>
              </>
            )}
          </div>

          {occupato && (
            <button
              type="button"
              data-testid="bg-ranked-annulla"
              onClick={ranked.cancel}
              className="min-h-11 rounded-full bg-bg-tertiary px-7 text-sm font-bold text-fg transition-opacity active:opacity-80"
            >
              {t('ranked.cancel')}
            </button>
          )}
        </div>
      </section>

      {/* ═══ LA CODA LIBERA — senza orologio e FUORI classifica (I-277) — e' una TESSERA della griglia
          sotto (I-282), non piu' una pill sotto l'anello. ⛔ Spenta la serratura la tessera NON c'e'
          (`libera` assente): una tessera spenta direbbe «esiste ma non ora», e finche' il server non
          conferma `rated: false` non e' vero nemmeno quello. */}
      {altri?.({
        inerte: occupato,
        libera: env.featureBackgammonUnrankedQueueEnabled ? entraLibera : undefined,
      })}

      {/* ⛔ Il foglio dice COSA SI SCRIVE, non «sei sicuro?»: una domanda generica
          non aggiunge informazione, aggiunge solo un tap. Copy di L-782, invariata. */}
      {conferma && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60"
          data-testid="conferma-classificata"
          onClick={() => setConferma(false)}
        >
          <div
            className="w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-bold text-fg">
              {t('ranked.confirmTitle', { defaultValue: 'Partita classificata' })}
            </h2>
            <p className="mt-2 text-sm text-fg-muted">
              {t('ranked.confirmBody', {
                defaultValue:
                  'Il risultato conta per la classifica e non si annulla — anche se abbandoni subito.',
              })}
            </p>
            <p className="mt-1 text-xs text-fg-muted">{t(queueLabelKey('clocked'))}</p>
            <button
              type="button"
              onClick={confermaIngresso}
              data-testid="conferma-classificata-entra"
              className="mt-4 w-full rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
            >
              {t('ranked.confirmEnter', { defaultValue: 'Entra in classificata' })}
            </button>
            <button
              type="button"
              onClick={() => setConferma(false)}
              className="mt-2 w-full rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              {t('ranked.confirmCancel', { defaultValue: 'Annulla' })}
            </button>
          </div>
        </div>
      )}

      {(ranked.notice || ranked.status === 'paired') && <RankedQueueSheet queue={ranked} />}
    </>
  );
}
